import React, { useEffect, useState, useRef } from "react";
import "./XYDiagram.scss";
import { useTranslation } from "react-i18next";
import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";
import StatisticsPatientModal from "../../StatisticsPatientModal";

const XYDiagramWellness = ({ values, property,propertyName, id, surveyOptions }) => {

  const { i18n } = useTranslation();
  const [statistics, setStatistics] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const hoverTimeoutRef = useRef(null);
  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  const maxSurveyOrder = Math.max(
    0,
    ...(Array.isArray(values) ? values.map((v) => Number(v?.surveyOrder || 0)) : [0])
  );

  const computedSurveyOptions = Array.from({ length: maxSurveyOrder }, (_, i) => {
    const id = i + 1;
    const existing = Array.isArray(surveyOptions)
      ? surveyOptions.find((o) => Number(o?.id) === id)
      : null;
    return existing ? existing : { id, selected: true };
  });

  // Layout constants
  const DOT_CENTER_OFFSET = 10;

  const computedStepX = (() => {
    return chartWidth / maxSurveyOrder;
  })();

  useEffect(() => {
    const next = (Array.isArray(values) ? values : []).filter((v) => getValue(v) >= 0);
    setStatistics(next);
  }, [values, id]);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const update = () => setChartWidth(el.offsetWidth || 0);
    update();

    // Keep width in sync (resizes / responsive / PDF render)
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleHoverEnter = (i) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredIndex(i);
  };

  const handleHoverLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredIndex(null);
      hoverTimeoutRef.current = null;
    }, 200); // small delay to allow moving into the tooltip
  };


  const calculateBottom = (index) => {
    if (isOdd(index)) {
      return "calc(175px - (" + (index + 1) * 15 + "px + 32px) )";
    } else {
      return "calc(175px - 32px )";
    }
  };

  const isOdd = (num) => {
    return num % 2;
  };

  const getValue = (value) => {
    let correctValue = null;
   if (id === 11) {
          correctValue = value.health_metric_1;
      } else if (id === 12) {
          correctValue = value.health_metric_2;
      } else if (id === 13) {
          correctValue = value.health_metric_3;
      } else if (id === 14) {
          correctValue = value.health_metric_4;
      } else if (id === 15) {
          correctValue = value.health_metric_5;
      } else if (id === 16) {
          correctValue = value.health_metric_6;
      }

    return correctValue;
  };

  const getIndexValue = (index) => {
    const filteredStatistics = statistics.filter((s) => s.visible !== false);

    let correctValue = null;
    if (filteredStatistics[index]) {
        if (id === 11) {
            correctValue = filteredStatistics[index].health_metric_1;
        } else if (id === 12) {
            correctValue = filteredStatistics[index].health_metric_2;
        } else if (id === 13) {
            correctValue = filteredStatistics[index].health_metric_3;
        } else if (id === 14) {
            correctValue = filteredStatistics[index].health_metric_4;
        } else if (id === 15) {
            correctValue = filteredStatistics[index].health_metric_5;
        } else if (id === 16) {
            correctValue = filteredStatistics[index].health_metric_6;
        }
    }

    const num = parseFloat(correctValue);
    return Number.isFinite(num) ? num : 0;
  };

  const getImprovement = (index) => {
    const filteredStatistics = statistics.filter((s) => s.visible !== false);

    if (
      filteredStatistics &&
      filteredStatistics[index] &&
      filteredStatistics[index - 1]
    ) {
      let firstValue = getIndexValue(0);
      let secondValue = getIndexValue(index);

      let calculation = 0;
      //is decrease
      if (secondValue < firstValue) {
        calculation = ((secondValue - firstValue) / firstValue) * 100;
      } else {
        calculation = ((secondValue - firstValue) / (100 - firstValue)) * 100;
      }

      if (isNaN(calculation)) {
        return 0;
      }
      return Math.round(calculation);
    } else {
      return 0;
    }
  };

  const getOverallImprovement = () => {
    if (!statistics) {
      return 0;
    }

    let total = 0;

    if (statistics) {
      statistics.forEach((v, index) => {
        if (v.visible !== false) {
          let firstValue = getIndexValue(0);
          let secondValue = getIndexValue(index);

          //is decrease
          if (secondValue < firstValue) {
            total = ((secondValue - firstValue) / firstValue) * 100;
          } else {
            total = ((secondValue - firstValue) / (100 - firstValue)) * 100;
          }
        }
      });
    }
    if (isNaN(total)) {
      total = 0;
    }
    return Math.round(total);
  };

  const getTitle = () => {
    return property;
  };

  const calculateDotLeft = (statistic) => {
    if (!statistic?.surveyOrder) return ((computedStepX/2)-11) + "px";
    return (Number(statistic.surveyOrder) - 1) * computedStepX + ((computedStepX/2)-11) + "px";
  };

  const calculateInfoLeft = (statistic) => {
    // 51 is half of the info container
    if (!statistic?.surveyOrder) return ((computedStepX/2)-11) - 51 + "px";
    return (Number(statistic.surveyOrder) - 1) * computedStepX + ((computedStepX/2)-11) - 51 + "px";
  };

  const calculateDotBottom = (statistic) => {
    if (getValue(statistic) === 0) {
      return 0;
    }

      return (500 / 100) * Math.round(getValue(statistic)) - 10 + "px";
  };

  const calculateInfoBottom = (statistic) => {
    const dotBottom = (500 / 100) * Math.round(getValue(statistic)) - 29;

    if (dotBottom < 387) {
      // bottom part of the graph
      return dotBottom + 32; // 67 is the height of info
    } else {
      // top part of the graph
      return dotBottom - 67 - 10; // 67 is the height of info
    }
  };

  const getColumnCenterX = (surveyOrder) => {
    if (!surveyOrder) return ((computedStepX/2)-11);
    return (Number(surveyOrder) - 1) * computedStepX + ((computedStepX/2)-11) + DOT_CENTER_OFFSET;
  };

  const renderLine = (from, to) => {
    if (!from || !to) return null;

    const x1 = getColumnCenterX(from.surveyOrder);
    const x2 = getColumnCenterX(to.surveyOrder);

    const y1 = (500 / 100) * Math.round(getValue(from));
    const y2 = (500 / 100) * Math.round(getValue(to));

    return (
      <line
        x1={x1 + 5}
        y1={500 - y1 - 15}
        x2={x2 + 5}
        y2={500 - y2 - 15}
        stroke="#979797"
        strokeWidth="4"
        strokeLinecap="round"
      />
    );
  };

  return (
    <div className={"globalBarChart"}>
      <div className={"BarChartTitle"}>{getTitle()}</div>

      <div className="XYDiagram">
        <div className={"XAxis"} style={{ marginTop: "-10px" }}>
          {[...Array(11)].map((r, index) => {
            return (
                <div className={"XAxisItem"} key={index}>
                  {100 - index * 10}
                </div>
            );
          })}
        </div>
        <div className={"XYDiagramInner"}>
          <div className={"mainXYDiagram"} ref={chartRef}>
            <div
              className={"XYDiagramBackground"}
              style={{
                  maxWidth:`${chartWidth}px`,
                marginTop: "17px",
                // Override the CSS class (flex+wrap) with a deterministic grid
                display: "grid",
                gridAutoFlow: "row",
                gridTemplateColumns: `repeat(${maxSurveyOrder || 1}, 1fr)`,
                gridTemplateRows: "repeat(10, 50px)",
                width: `${chartWidth}px`,
                boxSizing: "border-box",
              }}
            >
              {Array.from({ length: 10 * (maxSurveyOrder || 1) }).map((_, index) => {
                return (
                  <div
                    className={"XYDiagramBackgroundItem"}
                    key={index}
                    style={{
                        width:'100%',
                      height: "50px",
                      border: "1px solid #DED6C7",
                      boxSizing: "border-box",
                    }}
                  >
                      {/*{index}*/}
                  </div>
                );
              })}
            </div>
            <svg
              width={chartWidth}
              height={500}
              style={{ position: "absolute", left: 0, bottom: 0, zIndex: 2, overflow: "visible" }}
            >
              {statistics
                .filter((s) => s.visible !== false)
                .map((s, i, arr) => renderLine(s, arr[i + 1]))}
            </svg>
            {statistics
              ?.filter((s) => s.visible !== false)
              .map((s, index) => {
                return (
                  <div key={index} style={{zIndex:999}}>
                    <div
                      onMouseEnter={() => handleHoverEnter(index)}
                      onMouseLeave={handleHoverLeave}
                      style={{ position: 'relative' }}
                    >
                      <div
                        className={"resultDot"}
                        style={{
                          left: calculateDotLeft(s),
                          bottom: calculateDotBottom(s),
                        }}
                      />

                      {hoveredIndex === index ? (
                        <div
                          onMouseEnter={() => handleHoverEnter(index)}
                          onMouseLeave={handleHoverLeave}
                          className={"resultInfo"}
                          style={{
                            left: calculateInfoLeft(s),
                            bottom: calculateInfoBottom(s),
                          }}
                        >
                          <div className={"percentageOnly"}></div>
                          <div className={"valueTitle"}>{i18n.t("survey")} {s.surveyOrder}</div>
                          {index > 0 ? (
                            <div className={"valueSubtitle"}>{i18n.t("Improvement")}: {getImprovement(index)}%</div>
                          ) : null}
                          <div className={"valueSubtitle"}>Score: {Math.round(getValue(s))}</div>

                          <StatisticsPatientModal
                            data={values}
                            order={s.surveyOrder}
                            property={propertyName}
                            propertyLabel={property}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
          </div>
          <div className={"YAxis"}>
            {computedSurveyOptions.map((s, index) => {
              return (
                <div className={"YAxisItem"} style={{width:`${chartWidth/maxSurveyOrder}px`, textAlign:'center'}} key={index}>
                  {" "}
                  {i18n.t("survey")} {s.id}{" "}
                </div>
              );
            })}
          </div>
        </div>
        <div className={"overallImprovementXY"}>
          <div className={"overallImprovementNumber"}>
            {getOverallImprovement()}%
          </div>
          <div className={"overallImprovementText"}>
            {i18n.t("overallImprovement")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default XYDiagramWellness;
